import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { MISSION_DOCUMENT_ACCEPT } from "utils/missionDocuments";

function MissionDocumentsField({
  inputId = "mission-documents-upload",
  linkedDocument = null,
  pendingFiles = [],
  onAddFiles,
  onRemovePending,
  onRemoveLinked,
  disabled = false,
  error = "",
  uploadProgress = null,
  entityLabel = "mission",
}) {
  const handleChange = (e) => {
    onAddFiles?.(e.target.files);
    e.target.value = "";
  };

  return (
    <SoftBox>
      <input
        type="file"
        accept={MISSION_DOCUMENT_ACCEPT}
        multiple
        onChange={handleChange}
        style={{ display: "none" }}
        id={inputId}
        disabled={disabled}
      />
      <label htmlFor={inputId}>
        <SoftButton
          variant="outlined"
          color="secondary"
          component="span"
          disabled={disabled}
          startIcon={<Icon>upload_file</Icon>}
        >
          Add documents
        </SoftButton>
      </label>
      <SoftTypography
        variant="caption"
        color="secondary"
        display="block"
        mt={0.5}
      >
        PDF, Office files, and supporting images. These are attachments, not
        the mission header photo. They are linked to the {entityLabel} when
        saved.
      </SoftTypography>
      {error ? (
        <SoftTypography
          variant="caption"
          color="error"
          display="block"
          mt={0.5}
        >
          {error}
        </SoftTypography>
      ) : null}
      {uploadProgress ? (
        <SoftBox mt={1.5}>
          <SoftTypography variant="caption" display="block" mb={0.5}>
            Uploading {uploadProgress.name}… {uploadProgress.percent}%
          </SoftTypography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress.percent}
          />
        </SoftBox>
      ) : null}
      {linkedDocument?.fileName ? (
        <SoftBox
          mt={1.5}
          p={1.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={(theme) => ({
            borderRadius: "0.5rem",
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <SoftBox display="flex" alignItems="center" gap={1}>
            <Icon color="info">description</Icon>
            <SoftTypography variant="button" fontWeight="medium">
              {linkedDocument.originalName || linkedDocument.fileName}
            </SoftTypography>
            <SoftTypography variant="caption" color="secondary">
              (saved on {entityLabel})
            </SoftTypography>
          </SoftBox>
          <SoftButton
            variant="text"
            color="error"
            size="small"
            onClick={onRemoveLinked}
            disabled={disabled}
          >
            Remove
          </SoftButton>
        </SoftBox>
      ) : null}
      {pendingFiles.length > 0 ? (
        <SoftBox mt={1.5} display="flex" flexDirection="column" gap={1}>
          {pendingFiles.map((file, idx) => (
            <SoftBox
              key={`${file.name}-${file.size}-${idx}`}
              p={1.5}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={(theme) => ({
                borderRadius: "0.5rem",
                border: `1px dashed ${theme.palette.divider}`,
              })}
            >
              <SoftBox display="flex" alignItems="center" gap={1}>
                <Icon fontSize="small">schedule</Icon>
                <SoftTypography variant="caption">
                  {file.name} (uploads on save)
                </SoftTypography>
              </SoftBox>
              <SoftButton
                variant="text"
                color="error"
                size="small"
                onClick={() => onRemovePending?.(idx)}
                disabled={disabled}
              >
                Remove
              </SoftButton>
            </SoftBox>
          ))}
        </SoftBox>
      ) : null}
    </SoftBox>
  );
}

MissionDocumentsField.propTypes = {
  inputId: PropTypes.string,
  linkedDocument: PropTypes.shape({
    bucket: PropTypes.string,
    fileName: PropTypes.string,
    originalName: PropTypes.string,
  }),
  pendingFiles: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  onAddFiles: PropTypes.func,
  onRemovePending: PropTypes.func,
  onRemoveLinked: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  uploadProgress: PropTypes.shape({
    name: PropTypes.string,
    percent: PropTypes.number,
  }),
  entityLabel: PropTypes.string,
};

export default MissionDocumentsField;
